import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableCollectionBinding, useCollectionBinding } from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

function tourLabel(tour: VirtualTour | undefined, index: number): string {
  return tour?.virtual_tour_title?.trim() || `Tour ${index + 1}`;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={cn('vt-select__chevron', open && 'vt-select__chevron--open')}
      width="14"
      height="14"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7l3 3 5-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface TourSelectProps {
  tours: VirtualTour[];
  selectedIndex: number;
  onChange: (index: number) => void;
}

function TourSelect({ tours, selectedIndex, onChange }: TourSelectProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(selectedIndex);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    setHighlight(selectedIndex);

    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, selectedIndex, close]);

  const choose = (index: number) => {
    onChange(index);
    close();
  };

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => {
        const next =
          event.key === 'ArrowDown'
            ? Math.min(tours.length - 1, (open ? current : selectedIndex) + 1)
            : Math.max(0, (open ? current : selectedIndex) - 1);
        return next;
      });
      return;
    }
    if (!open) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(highlight);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setHighlight(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setHighlight(tours.length - 1);
    }
  };

  const activeLabel = tourLabel(tours[selectedIndex], selectedIndex);
  const activeOptionId = `${listId}-opt-${highlight}`;

  return (
    <div className="vt-select" ref={rootRef}>
      <span className="vt-select__eyebrow" id={`${listId}-label`}>
        Select a Tour
      </span>
      <button
        type="button"
        className={cn('vt-select__trigger', open && 'vt-select__trigger--open')}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${listId}-label`}
        aria-controls={listId}
        aria-activedescendant={open ? activeOptionId : undefined}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="vt-select__value">{activeLabel}</span>
        <ChevronIcon open={open} />
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={`${listId}-label`}
          className="vt-select__menu"
        >
          {tours.map((tour, index) => {
            const isSelected = index === selectedIndex;
            const isHighlighted = index === highlight;
            return (
              <li
                key={`${tour.virtual_tour_url || tour.virtual_tour_title || index}`}
                id={`${listId}-opt-${index}`}
                role="option"
                aria-selected={isSelected}
                className={cn(
                  'vt-select__option',
                  isSelected && 'vt-select__option--selected',
                  isHighlighted && 'vt-select__option--highlight',
                )}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(index)}
              >
                <span>{tourLabel(tour, index)}</span>
                {isSelected && <CheckIcon />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const safeIndex = validTours.length === 0 ? 0 : Math.min(selectedIndex, validTours.length - 1);
  const activeTour = validTours[safeIndex];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white py-8 md:py-12">
      <EditableCollectionBinding bindingPath="virtualtourembed.tours" label="Virtual Tours">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {hasMultiple && (
            <div className="mb-5 flex justify-end md:mb-6">
              <TourSelect
                tours={validTours}
                selectedIndex={safeIndex}
                onChange={setSelectedIndex}
              />
            </div>
          )}

          {activeTour?.virtual_tour_url ? (
            <div
              className="relative w-full overflow-hidden rounded-lg border border-border"
              style={{ paddingBottom: '56.25%' }}
            >
              <iframe
                key={activeTour.virtual_tour_url}
                src={activeTour.virtual_tour_url}
                className="absolute inset-0 h-full w-full border-0"
                allowFullScreen
                title={activeTour.virtual_tour_title || 'Virtual Tour'}
              />
            </div>
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted">
              <p className="font-body text-muted-foreground">
                No virtual tours are available for this property.
              </p>
            </div>
          )}
        </div>
      </EditableCollectionBinding>

      <style>{`
        .vt-select {
          position: relative;
          width: min(100%, 22rem);
        }
        .vt-select__eyebrow {
          display: block;
          margin-bottom: 0.4rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary);
        }
        .vt-select__trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          width: 100%;
          padding: 0.7rem 0.95rem;
          /* White/default page chrome — not --vibe-color-surface, which can
             resolve to property_color_3 (a brand tint) per site. Matches the
             Belmont parent select (bg-white) and other theme form controls. */
          background: var(--vibe-color-background, #ffffff);
          border: 1px solid var(--vibe-color-border);
          border-bottom-width: 2px;
          border-bottom-color: var(--vibe-color-primary);
          border-radius: var(--vibe-radius-md);
          cursor: pointer;
          text-align: left;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        .vt-select__trigger:hover,
        .vt-select__trigger--open {
          background: var(--vibe-color-background, #ffffff);
          border-color: var(--vibe-color-primary);
          box-shadow: var(--vibe-shadow-sm);
        }
        .vt-select__trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .vt-select__value {
          min-width: 0;
          flex: 1;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .vt-select__chevron {
          flex-shrink: 0;
          color: var(--vibe-color-primary);
          transition: transform 0.2s ease;
        }
        .vt-select__chevron--open {
          transform: rotate(180deg);
        }
        .vt-select__menu {
          position: absolute;
          top: calc(100% + 0.35rem);
          left: 0;
          right: 0;
          z-index: 20;
          margin: 0;
          padding: 0.35rem 0;
          list-style: none;
          background: var(--vibe-color-background, #ffffff);
          border: 1px solid var(--vibe-color-border);
          border-radius: var(--vibe-radius-md);
          box-shadow: var(--vibe-shadow-lg);
          overflow: hidden;
        }
        .vt-select__option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          width: 100%;
          padding: 0.7rem 0.95rem;
          border: none;
          background: transparent;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text);
          cursor: pointer;
          text-align: left;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .vt-select__option--highlight {
          background: color-mix(in srgb, var(--vibe-color-primary) 6%, var(--vibe-color-background, #ffffff));
        }
        .vt-select__option--selected,
        .vt-select__option--selected.vt-select__option--highlight {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          font-weight: 600;
        }
        .vt-select__option--selected svg {
          color: var(--vibe-color-text-on-primary);
          flex-shrink: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .vt-select__trigger,
          .vt-select__chevron,
          .vt-select__option {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default VirtualTourEmbed;
